import React from "react";
import colors from "common/styles/colors";
import { ClimateRiskType } from "../../types/appraiser.types";
import {
  floodRiskFromZoneAnswers,
  isIn10YearFloodZone,
  isIn100YearFloodZone,
  isIn1000YearFloodZone,
} from "../../helpers/climateRisk.helpers";

const YesNoAnswer: React.FC<{
  value?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const isEdit = !!onChange;

  if (!isEdit) {
    const answerStyle = (selected: boolean): React.CSSProperties => ({
      fontWeight: selected ? 600 : 400,
      color: selected ? colors.black : colors.grey,
    });

    return (
      <div
        style={{
          fontSize: 14,
          lineHeight: "20px",
          whiteSpace: "nowrap",
          flexShrink: 0,
          pointerEvents: "none",
        }}
      >
        <span style={answerStyle(value === false)}>ne</span>
        {" / "}
        <span style={answerStyle(value === true)}>taip</span>
      </div>
    );
  }

  const optionStyle = (selected: boolean): React.CSSProperties => ({
    minWidth: 52,
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
    textAlign: "center",
    color: selected ? colors.white : colors.grey,
    backgroundColor: selected ? colors.purple2 : colors.white,
    cursor: "pointer",
    userSelect: "none",
  });

  return (
    <div
      style={{
        display: "flex",
        flexShrink: 0,
        border: `1px solid ${colors.greyborder}`,
        borderRadius: 8,
        overflow: "hidden",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <div style={optionStyle(value === false)} onClick={() => onChange(false)}>
        Ne
      </div>
      <div
        style={{
          ...optionStyle(value === true),
          borderLeft: `1px solid ${colors.greyborder}`,
        }}
        onClick={() => onChange(true)}
      >
        Taip
      </div>
    </div>
  );
};

const floodZoneLabel = (years: string, percent: string) => (
  <>
    Turtas patenka į zoną, kur potvynio tikimybė 1 kartą per{" "}
    <span style={{ whiteSpace: "nowrap" }}>
      {years} metų ({percent} tikimybė)
    </span>
  </>
);

const FloodZoneRow: React.FC<{
  label: React.ReactNode;
  value?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => {
  const isEdit = !!onChange;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        columnGap: 16,
        alignItems: isEdit ? "center" : "start",
      }}
    >
      <div
        style={{
          fontSize: 14,
          lineHeight: "20px",
          color: colors.black,
        }}
      >
        {label}
      </div>
      <YesNoAnswer value={value} onChange={onChange} disabled={disabled} />
    </div>
  );
};

interface FloodZoneCriteriaProps {
  floodRisk?: ClimateRiskType | null;
  onChange?: (floodRisk: ClimateRiskType) => void;
  disabled?: boolean;
}

export const FloodZoneCriteria: React.FC<FloodZoneCriteriaProps> = ({
  floodRisk,
  onChange,
  disabled,
}) => {
  const knownRisk =
    floodRisk && Object.values(ClimateRiskType).includes(floodRisk)
      ? floodRisk
      : undefined;

  const in10Year = knownRisk ? isIn10YearFloodZone(knownRisk) : undefined;
  const in100Year = knownRisk ? isIn100YearFloodZone(knownRisk) : undefined;
  const in1000Year = knownRisk ? isIn1000YearFloodZone(knownRisk) : undefined;

  const updateZones = (
    next10: boolean,
    next100: boolean,
    next1000: boolean
  ) => {
    onChange?.(floodRiskFromZoneAnswers(next10, next100, next1000));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <FloodZoneRow
        label={floodZoneLabel("10", "10 %")}
        value={in10Year}
        disabled={disabled}
        onChange={
          onChange
            ? (in10) =>
                updateZones(
                  in10,
                  in10 ? true : in100Year === true,
                  in10 ? true : in1000Year === true
                )
            : undefined
        }
      />
      <FloodZoneRow
        label={floodZoneLabel("100", "1 %")}
        value={in100Year}
        disabled={disabled}
        onChange={
          onChange
            ? (in100) =>
                updateZones(
                  in100 ? in10Year === true : false,
                  in100,
                  in100 ? true : in1000Year === true
                )
            : undefined
        }
      />
      <FloodZoneRow
        label={floodZoneLabel("1000", "0.1 %")}
        value={in1000Year}
        disabled={disabled}
        onChange={
          onChange
            ? (in1000) =>
                updateZones(
                  in1000 ? in10Year === true : false,
                  in1000 ? in100Year === true : false,
                  in1000
                )
            : undefined
        }
      />
    </div>
  );
};
